/* US-only counter. Shopify metafield remains the displayed source of truth. */
(() => {
  if (window.bwViewsInstalled) return;
  window.bwViewsInstalled = true;
  const storageKey = 'bw_views_nonce_v3', sent = new Set(), jobs = new Set();
  let turnstileReady;
  let pollTimer, pollController, pollBusy = false, generation = 0, failureDelay = 5000, pageActive = true;
  const consent = () => { try { return Shopify.customerPrivacy.analyticsProcessingAllowed() === true; } catch { return false; } };
  const live = () => ['blitzwayusa.com', 'www.blitzwayusa.com'].includes(location.hostname) &&
    window.Shopify?.theme?.role === 'main' && !Shopify.designMode && !Shopify.previewMode &&
    !['preview_theme_id', '_fd', 'preview'].some(k => new URLSearchParams(location.search).has(k));
  const active = () => pageActive && live() && consent() && document.visibilityState === 'visible';
  function endpointFor(element) {
    try { const url = new URL(element.dataset.endpoint);
      return url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash && url.pathname === '/' ? url : null;
    } catch { return null; }
  }
  function show(element, data) {
    if (active() && Number.isSafeInteger(data?.views) && data.views >= 0) element.textContent = 'Views: ' + data.views.toLocaleString('en-US');
  }
  function pausePolling() { clearTimeout(pollTimer); pollTimer = undefined; generation++; pollController?.abort(); }
  async function poll() {
    pollTimer = undefined;
    if (!active() || pollBusy) return;
    pollBusy = true; const turn = generation, controller = new AbortController(); pollController = controller;
    const timeout = setTimeout(() => controller.abort(), 4000); let failed = false;
    try {
      for (const element of document.querySelectorAll('[data-bw-counter="custom"]')) {
        const endpoint = endpointFor(element), id = element.dataset.productId;
        if (!endpoint || !/^[1-9]\d{0,19}$/.test(id || '')) continue;
        const response = await fetch(new URL('v2/views/' + id, endpoint), { credentials: 'omit', cache: 'no-store', signal: controller.signal });
        if (!response.ok) { failed = true; continue; }
        const data = await response.json(); if (turn === generation) show(element, data);
      }
    } catch { failed = true; }
    finally {
      clearTimeout(timeout); pollBusy = false; if (pollController === controller) pollController = undefined;
      failureDelay = failed ? Math.min(failureDelay * 2, 60000) : 5000;
      if (active()) schedulePoll();
    }
  }
  function schedulePoll() {
    if (!active() || pollTimer !== undefined || pollBusy) return;
    pollTimer = setTimeout(poll, failureDelay); pollTimer?.unref?.();
  }
  function nonce() {
    try {
      let saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (!saved || !/^[a-f0-9]{32}$/.test(saved.id) || !Number.isFinite(saved.at) || Date.now() < saved.at || Date.now() - saved.at >= 86400000) {
        saved = { id: [...crypto.getRandomValues(new Uint8Array(16))].map(n => n.toString(16).padStart(2, '0')).join(''), at: Date.now() };
        localStorage.setItem(storageKey, JSON.stringify(saved));
      }
      return JSON.parse(localStorage.getItem(storageKey)).id === saved.id ? saved.id : null;
    } catch { return null; }
  }
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve();
    if (!turnstileReady) turnstileReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timer = setTimeout(() => reject(Error('challenge_timeout')), 10000);
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true; script.onload = () => { clearTimeout(timer); window.turnstile ? resolve() : reject(Error('challenge_missing')); };
      script.onerror = () => { clearTimeout(timer); reject(Error('challenge_unavailable')); };
      document.head.appendChild(script);
    });
    return turnstileReady;
  }
  async function record(element) {
    const id = element.dataset.productId;
    if (!/^[1-9]\d{0,19}$/.test(id || '') || sent.has(id)) return;
    let endpoint;
    try {
      endpoint = new URL(element.dataset.endpoint);
      if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password || endpoint.search || endpoint.hash || endpoint.pathname !== '/') return;
    } catch { return; }
    if (!element.dataset.sitekey) return;
    const visitor = nonce(); if (!visitor) return;
    sent.add(id);
    const controller = new AbortController(); jobs.add(controller);
    let widget, container, timer, requestStarted = false;
    try {
      await loadTurnstile();
      if (controller.signal.aborted || !consent() || !live()) return;
      container = document.createElement('div'); element.insertAdjacentElement('afterend', container);
      const token = await new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(Error('challenge_timeout')), 15000);
        controller.signal.addEventListener('abort', () => reject(Error('cancelled')), { once: true });
        widget = window.turnstile.render(container, { sitekey: element.dataset.sitekey, action: 'product-view', cData: id,
          execution: 'execute', appearance: 'interaction-only', size: 'compact',
          callback: resolve, 'error-callback': () => { reject(Error('challenge_failed')); return true; },
          'expired-callback': () => reject(Error('challenge_expired')) });
        window.turnstile.execute(widget);
      });
      clearTimeout(timer);
      if (controller.signal.aborted || !consent() || !live()) return;
      timer = setTimeout(() => controller.abort(), 10000);
      requestStarted = true;
      const response = await fetch(new URL('v2/event', endpoint).href, { method: 'POST', credentials: 'omit', cache: 'no-store',
        headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
        body: JSON.stringify({ productId: id, nonce: visitor, token, consent: true, themeRole: 'main' }) });
      if (response.ok && response.status !== 204) {
        const data = await response.json(); pausePolling(); show(element, data); failureDelay = 5000; schedulePoll();
      }
    } catch { /* Keep the saved metafield visible. Never affect purchase controls. */ }
    finally {
      clearTimeout(timer); jobs.delete(controller);
      // A cancelled challenge has not submitted a visit. Allow a later consent grant
      // to try again; keep submitted requests marked even if their response is lost.
      if (!requestStarted) sent.delete(id);
      if (widget !== undefined) window.turnstile?.remove(widget); container?.remove();
    }
  }
  function start() {
    if (!active()) return;
    document.querySelectorAll('[data-bw-counter="custom"]').forEach(element => { void record(element); });
    if (pollTimer === undefined && !pollBusy) void poll();
  }
  function stop() { jobs.forEach(job => job.abort()); pausePolling(); }
  document.addEventListener('visitorConsentCollected', () => {
    if (consent()) start(); else { stop(); try { localStorage.removeItem(storageKey); } catch {} }
  });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') start(); else stop(); });
  window.addEventListener('pageshow', () => { pageActive = true; start(); });
  window.addEventListener('pagehide', () => { pageActive = false; stop(); });
  if (live() && !window.Shopify.customerPrivacy && typeof Shopify.loadFeatures === 'function') {
    Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) start(); });
  }
  start();
})();
